/**
 * Call Panel Component
 *
 * Video/audio call interface with local and remote video display.
 */
import { useEffect, useRef } from 'react';
import type { ActiveCall, IncomingCall } from '../hooks/useCalls';
import { Avatar } from './Avatar';
import './CallPanel.css';

interface CallPanelProps {
  activeCall: ActiveCall | null;
  incomingCall: IncomingCall | null;
  onAccept: () => void;
  onDecline: (reason?: string) => void;
  onEndCall: () => void;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  setLocalVideoRef: (el: HTMLVideoElement | null) => void;
  setRemoteVideoRef: (el: HTMLVideoElement | null) => void;
  getDisplayName: (pubkey: string) => string;
  getAvatarUrl?: (pubkey: string) => string | undefined;
}

export function CallPanel({
  activeCall,
  incomingCall,
  onAccept,
  onDecline,
  onEndCall,
  onToggleVideo,
  onToggleAudio,
  setLocalVideoRef,
  setRemoteVideoRef,
  getDisplayName,
  getAvatarUrl,
}: CallPanelProps) {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  // Wire up video refs
  useEffect(() => {
    setLocalVideoRef(localVideoRef.current);
    return () => setLocalVideoRef(null);
  }, [setLocalVideoRef]);

  useEffect(() => {
    setRemoteVideoRef(remoteVideoRef.current);
    return () => setRemoteVideoRef(null);
  }, [setRemoteVideoRef]);

  // Format call duration
  const formatDuration = (startTime: number) => {
    const seconds = Math.floor((Date.now() - startTime) / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Incoming call UI
  if (incomingCall && !activeCall) {
    return (
      <div className="call-panel incoming">
        <div className="call-overlay">
          <div className="incoming-call-card">
            <Avatar
              src={getAvatarUrl?.(incomingCall.callerPubkey)}
              displayName={getDisplayName(incomingCall.callerPubkey)}
              size="large"
            />
            <div className="caller-info">
              <h3>{getDisplayName(incomingCall.callerPubkey)}</h3>
              <p className="call-type">
                {incomingCall.video ? 'Video' : 'Voice'} call
              </p>
            </div>
            <div className="incoming-actions">
              <button
                className="call-btn decline"
                onClick={() => onDecline()}
                title="Decline"
              >
                <span className="btn-icon">✕</span>
              </button>
              <button
                className="call-btn accept"
                onClick={onAccept}
                title="Accept"
              >
                <span className="btn-icon">✓</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active call UI
  if (activeCall) {
    return (
      <div className="call-panel active">
        <div className="call-videos">
          {/* Remote video (full size) */}
          <div className="remote-video-container">
            {activeCall.remoteStream ? (
              <video
                ref={remoteVideoRef}
                className="remote-video"
                autoPlay
                playsInline
              />
            ) : (
              <div className="waiting-for-video">
                <Avatar
                  src={getAvatarUrl?.(activeCall.peerPubkey)}
                  displayName={getDisplayName(activeCall.peerPubkey)}
                  size="large"
                />
                <p>{activeCall.status === 'calling' ? 'Calling...' : 'Connecting...'}</p>
              </div>
            )}
          </div>

          {/* Local video (picture-in-picture) */}
          <div className="local-video-container">
            <video
              ref={localVideoRef}
              className="local-video"
              autoPlay
              playsInline
              muted
            />
          </div>
        </div>

        {/* Call info bar */}
        <div className="call-info-bar">
          <div className="call-peer-info">
            <span className="peer-name">{getDisplayName(activeCall.peerPubkey)}</span>
            {activeCall.startTime && (
              <span className="call-duration">{formatDuration(activeCall.startTime)}</span>
            )}
          </div>
        </div>

        {/* Call controls */}
        <div className="call-controls">
          <button
            className={`control-btn ${!activeCall.audio ? 'off' : ''}`}
            onClick={onToggleAudio}
            title={activeCall.audio ? 'Mute' : 'Unmute'}
          >
            {activeCall.audio ? '🎤' : '🔇'}
          </button>
          <button
            className={`control-btn ${!activeCall.video ? 'off' : ''}`}
            onClick={onToggleVideo}
            title={activeCall.video ? 'Turn off camera' : 'Turn on camera'}
          >
            {activeCall.video ? '📹' : '📷'}
          </button>
          <button
            className="control-btn end-call"
            onClick={onEndCall}
            title="End call"
          >
            📞
          </button>
        </div>
      </div>
    );
  }

  // No call - don't render anything
  return null;
}

/**
 * Small call button to initiate calls from other UIs
 */
interface CallButtonProps {
  peerPubkey: string;
  onStartCall: (pubkey: string, options: { video?: boolean; audio?: boolean }) => void;
  disabled?: boolean;
}

export function CallButton({ peerPubkey, onStartCall, disabled }: CallButtonProps) {
  return (
    <div className="call-buttons">
      <button
        className="call-action-btn voice"
        onClick={() => onStartCall(peerPubkey, { audio: true, video: false })}
        disabled={disabled}
        title="Voice call"
      >
        🎤
      </button>
      <button
        className="call-action-btn video"
        onClick={() => onStartCall(peerPubkey, { audio: true, video: true })}
        disabled={disabled}
        title="Video call"
      >
        📹
      </button>
    </div>
  );
}
